import type { AdaptedOperationModel, OperationModel } from '@app/models/api/OperationModel';
import { get } from 'svelte/store';
import { t } from 'svelte-i18n';
import { toStringSeparateByComma } from '$lib/utils/arrays';
import ProjectHeaderInfo from '@app/components/projects/elements/ProjectHeaderInfo.svelte';
import { enumToDictionary, formatAmount } from '$lib/utils/strings';
import type { ComponentType, PerformanceType, TabsProps } from '$lib/types/props';
import { round } from '$lib/utils/number';
import type { CompanyModel } from '@app/models/api/CompanyModel';
import { getOperations, getOperationSimulation } from '@app/services/api/operations';
import { urls } from '@app/urls';
import { goto } from '$app/navigation';
import { isDateAfter, isDateBefore, isDateEqual, parseApiDate } from '$lib/utils/date';
import {
	OperationStatusEnum,
	OperationStatusEnumFR
} from '@app/models/api/enums/OperationStatusEnum';
import type { OperationSummaryModel } from '@app/models/api/OperationSummaryModel';
import type { ComponentsObj } from '@app/types/Global';
import { OperationRegulationsEnum } from '@app/models/api/enums/OperationRegulationsEnum';
import {
	OperationFinancialInstrumentEnum,
	OperationFinancialInstrumentEnumFR
} from '@app/models/api/enums/OperationFinancialInstrumentEnum';
import { logError } from '@app/utils/log';
import type { OperationCollectDataParams, OperationCollectDataResult } from '@app/types/Operation';
import { snackbar } from '@app/lib/stores';

export const isCollectComing = (o: OperationSummaryModel | OperationModel) => {
	if (isOperationSummaryModel(o)) {
		return !!o.fundRaisingStartDate && isDateAfter(o.fundRaisingStartDate);
	} else {
		return !!o.collectStartDate && isDateAfter(o.collectStartDate);
	}
};

export const isCollectEnded = (o: OperationSummaryModel | OperationModel) => {
	if (isOperationSummaryModel(o)) {
		return isDateAfter(new Date(), o.fundRaisingEndDate);
	} else if (o.collectEndDate) {
		return isDateAfter(new Date(), o.collectEndDate);
	}
};

export const isCollectAlmostEnd = (o: OperationSummaryModel | OperationModel) => {
	if (isOperationSummaryModel(o)) {
		return !!(
			o.fundRaisingStartDate &&
			o.fundRaisingEndDate &&
			isDateBefore(o.fundRaisingStartDate) &&
			isDateAfter(o.fundRaisingEndDate)
		);
	} else {
		return !!(
			o.collectStartDate &&
			o.collectEndDate &&
			isDateBefore(o.collectStartDate) &&
			isDateAfter(o.collectEndDate)
		);
	}
};

export const isCollectRunning = (o: OperationSummaryModel | OperationModel) => {
	if (isOperationSummaryModel(o)) {
		return (
			!o.fundRaisingStartDate || (!o.fundRaisingEndDate && isDateBefore(o.fundRaisingStartDate))
		);
	} else {
		return !o.collectStartDate || (!o.collectEndDate && isDateBefore(o.collectStartDate));
	}
};

export const getTaxExemptionString = (o: OperationModel): string => {
	const a: string[] = [];
	if (o.isIRPPTaxExempted) {
		a.push(
			get(t)('operation.taxExemption.IRPP', {
				values: { pourcentTaxExemption: o.IRPPTaxExemptionRate ?? 18 }
			})
		);
	}
	if (o.isDisposalContributionTaxExempted) {
		a.push(get(t)('operation.taxExemption.disposalContribution'));
	}

	return a.length ? toStringSeparateByComma(a) : get(t)('operation.taxExemption.none');
};

export const canInvest = (o: OperationModel) => {
	const hasStartDate = o.collectStartDate !== undefined;
	const hasEndDate = o.collectEndDate !== undefined;
	return (
		(!o.investErrors || (o.investErrors?.length === 1 && o.investErrors[0]?.code === 119)) &&
		o.status.includes(OperationStatusEnum.FUNDING) &&
		(!hasStartDate || isDateBefore(o.collectStartDate) || isDateEqual(o.collectStartDate)) &&
		(!hasEndDate || isDateAfter(o.collectEndDate))
	);
};

export const getTaxExemptionArray = (o: OperationModel): string[] => {
	const a: string[] = [];
	if (o.isIRPPTaxExempted) {
		a.push(
			get(t)('operation.taxExemption.IRPP', {
				values: { pourcentTaxExemption: o.IRPPTaxExemptionRate ?? 18 }
			})
		);
	}
	if (o.isDisposalContributionTaxExempted)
		a.push(get(t)('operation.taxExemption.disposalContribution'));

	return a;
};

export const isTaxExempted = (o: OperationModel): boolean => {
	return o.isIRPPTaxExempted;
};

export const getTaxExemptionRate = (o: OperationModel): number => {
	return o.IRPPTaxExemptionRate;
};

export const getPerformance = async (
	o: OperationModel,
	amount: number
): Promise<PerformanceType> => {
	if (isTaxExempted(o)) {
		const exemptionPercentage = (getTaxExemptionRate(o) / 100) * 95;
		return { value: (amount * exemptionPercentage) / 100 };
	} else {
		const data = await getOperationSimulation(o.id.toString(), amount);
		return {
			value: data.rendementATermeReel,
			rateOfReturn: data.tauxRendement,
			nbMonths: data.dureeEnMois
		};
	}
};

export const getAmountNeededOperationModel = (o: OperationModel): number => {
	return (
		(o.amountNeededWiseeders ?? 0) +
		(o.amountNeededPremium ?? 0) +
		(o.amountReserveInstitutional ?? 0) +
		(hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(o)
			? o.amountReachedExcludingWiseed
			: 0)
	);
};
export const getAmountNeededSummary = (o: OperationSummaryModel) => {
	return (
		o.amountToSeek +
		(hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(o)
			? o.amountReachedExcludingWiseed
			: 0)
	);
};

export const isMinAmountReachedFn = (
	operation: OperationModel | OperationSummaryModel
): boolean => {
	return hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(operation)
		? operation.amountCollected + operation.amountReachedExcludingWiseed >=
				operation.amountNeededMin + operation.amountReachedExcludingWiseed
		: operation.amountCollected >= operation.amountNeededMin;
};

export const getOperationModelAmountNeeded = (o: OperationModel): number => {
	return (
		(o.amountNeededWiseeders ?? 0) +
		(o.amountNeededPremium ?? 0) +
		(o.amountReserveInstitutional ?? 0) +
		(hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(o)
			? o.amountReachedExcludingWiseed
			: 0)
	);
};

export const isGoalAmountReachedFn = (
	operation: OperationModel | OperationSummaryModel
): boolean => {
	const operationAmount = isOperationSummaryModel(operation)
		? getAmountNeededSummary(operation)
		: getOperationModelAmountNeeded(operation);
	return hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(operation)
		? operation.amountCollected + operation.amountReachedExcludingWiseed >= operationAmount
		: operation.amountCollected >= operationAmount;
};

export const hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks = (
	o: OperationModel | OperationSummaryModel
): boolean => {
	return (
		o.amountReachedExcludingWiseed !== undefined &&
		o.amountReachedExcludingWiseed !== 0 &&
		o.financialInstrument === OperationFinancialInstrumentEnum.STOCKS
	);
};

export const getPercentageCollectedOutsideBeforeAmountMin = (
	o: OperationModel | OperationSummaryModel
): number => {
	if (o.amountReachedExcludingWiseed >= o.amountNeededMin + o.amountReachedExcludingWiseed) {
		return 100;
	} else {
		return (
			(o.amountReachedExcludingWiseed / (o.amountNeededMin + o.amountReachedExcludingWiseed)) * 100
		);
	}
};

export const getPercentageCollectedBeforeAmountMin = (
	o: OperationModel | OperationSummaryModel
): number => {
	if (!o.amountCollected) {
		return 0;
	} else if (o.amountCollected >= o.amountNeededMin) {
		return hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(o)
			? 100 - getPercentageCollectedOutsideBeforeAmountMin(o)
			: 100;
	} else {
		return (
			(o.amountCollected /
				(o.amountNeededMin +
					(hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(o)
						? o.amountReachedExcludingWiseed
						: 0))) *
			100
		);
	}
};

export const getPercentageCollectedAfterAmountMin = (
	o: OperationModel | OperationSummaryModel
): number => {
	const operationAmount = isOperationSummaryModel(o)
		? getAmountNeededSummary(o)
		: getOperationModelAmountNeeded(o);
	if (
		o.amountCollected +
			(hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(o)
				? o.amountReachedExcludingWiseed
				: 0) <=
		(hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(o)
			? o.amountNeededMin + o.amountReachedExcludingWiseed
			: o.amountNeededMin)
	) {
		return 0;
	} else {
		return (
			((o.amountCollected +
				(hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(o)
					? o.amountReachedExcludingWiseed
					: 0) -
				(hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(o)
					? o.amountNeededMin + o.amountReachedExcludingWiseed
					: o.amountNeededMin)) /
				(operationAmount -
					(hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(o)
						? o.amountNeededMin + o.amountReachedExcludingWiseed
						: o.amountNeededMin))) *
			100
		);
	}
};

export const getEntryFees = (o: OperationModel): number => {
	return o.percentageFees ? o.percentageFees : o.percentageCostForWicap;
};

export const createFinancialInstrumentHeaderComponent = (o: OperationModel): ComponentType => {
	return {
		component: ProjectHeaderInfo as never,
		props: {
			label: get(t)('operation.headers.financialInstrument.label'),
			value: getInstrumentLabel(o),
			url: urls.INVESTORS.HOW_TO_INVEST
		}
	};
};
export const getInstrumentLabel = (operation: OperationModel): string => {
	const instrumentType = enumToDictionary(operation.financialInstrument);
	const instrumentLabel = get(t)(`financialInstrument.${instrumentType}`);
	const options: string[] = [];

	if (operation.isConvertibleBonds) {
		options.push('convertibles');
	}

	if (operation.isVariableRate) {
		options.push('à taux variable');
	}

	return options.length > 0 ? `${instrumentLabel} ${options.join(' ')}` : instrumentLabel;
};

export const getFinancialStructureTotalAmount = (o: OperationModel) => {
	return (
		(o.scMontantFondsPropresCreation ?? 0) +
		(o.scMontantQuasiFondsPropresLevee ?? 0) +
		(o.scMontantFondsPropresLeveeHolding ?? 0) +
		(o.scMontantPretBancaireDette ?? 0) +
		(o.scMontantCommercialisation ?? 0)
	);
};

export const getTotalEquityAmount = (o: OperationModel) => {
	return (o.scMontantQuasiFondsPropresLevee ?? 0) + (o.scMontantFondsPropresCreation ?? 0);
};

export const getEquityPercentage = (o: OperationModel): number => {
	return round((o.scMontantFondsPropresCreation / getTotalEquityAmount(o)) * 100, 1);
};

export const getSemiEquityPercentage = (o: OperationModel): number => {
	return round((o.scMontantQuasiFondsPropresLevee / getTotalEquityAmount(o)) * 100, 1);
};

export const getTotalCostPrice = (o: OperationModel): number => {
	return (
		(o.chiffresHonorairesGestion ?? 0) +
		(o.chiffresFraisGeneraux ?? 0) +
		(o.chiffresCoutTravauxEtudes ?? 0) +
		(o.chiffresChargesFoncieres ?? 0)
	);
};

export const getMarginAmount = (o: OperationModel): number => {
	return o.chiffresCA - getTotalCostPrice(o);
};

export const getMarginPercent = (o: OperationModel): string => {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-call
	return o.chiffresCA
		? `${formatAmount((getMarginAmount(o) / o.chiffresCA) * 100)}%`
		: get(t)('noContent');
};

export const getCompanyInfos = (c: CompanyModel): string => {
	return `${c.legalStatus} - ${c.registrationNum} ${c.registrationCity}`;
};

export const isOperator = (o: OperationModel, c: CompanyModel) => {
	return (o.operator && c.id === o.operator.id) || (o.operator2 && c.id === o.operator2.id);
};

export const isGuarantor = (o: OperationModel, c: CompanyModel) => {
	return (
		(o.guarantor1 && c.id === o.guarantor1.id) ||
		(o.guarantor2 && c.id === o.guarantor2.id) ||
		(o.guarantor3 && c.id === o.guarantor3.id) ||
		(o.guarantor4 && c.id === o.guarantor4.id) ||
		(o.guarantor5 && c.id === o.guarantor5.id)
	);
};

export const isSharesTransmitters = (o: OperationModel, c: CompanyModel) => {
	return o.sharesTransmitters && c.id === o.sharesTransmitters.id;
};

export const isFundReceiver = (o: OperationModel, c: CompanyModel) => {
	return o.recipientOfFunds && o.recipientOfFunds.id === c.id;
};

export const isUserOfFund = (o: OperationModel, c: CompanyModel) => {
	return o.userOfFunds && o.userOfFunds.id === c.id;
};

export const isProjectCompany = (o: OperationModel, c: CompanyModel) => {
	return o.projectCompany && c.id === o.projectCompany.id;
};

export const getPaymentMethods = (o: OperationModel): string[] => {
	const methods: string[] = [];

	if (o.isCreditCardPayment) {
		methods.push(get(t)('subscription.paymentMethod.CREDIT_CARD.title'));
	}

	if (o.isChequePayment) {
		methods.push(get(t)('subscription.paymentMethod.CHEQUE.title'));
	}

	if (o.isWalletPayment) {
		methods.push(get(t)('subscription.paymentMethod.WALLET.title'));
	}

	if (o.isPEATransferPayment) {
		methods.push(get(t)('subscription.paymentMethod.TRANSFER_PEA.title'));
	}

	return methods;
};

export const getSlugFromOperations = async (fetch): Promise<any> => {
	const arrayOperationSlug: { full_slug: string }[] = [];
	try {
		const operations = await getOperations(
			`statuts=${OperationStatusEnumFR.EN_FINANCEMENT}`,
			false,
			fetch
		);
		operations?.data.forEach((operation) => {
			arrayOperationSlug.push({ full_slug: `projet/${operation.id}` });
		});
	} catch (error) {
		logError('Error => ', error);
	}

	return arrayOperationSlug;
};

export const getDynamicTabs = (o: OperationModel): TabsProps => {
	const tabs: TabsProps = [];

	(o.amountSubscribed > 0 ||
		o?.status?.includes(OperationStatusEnum.FUNDING) ||
		o?.status?.includes(OperationStatusEnum.VOTE)) &&
		tabs.push({
			key: 'questions',
			title: {
				label: get(t)('operation.tabs.questions'),
				nbItems: o.nbComments,
				onClick: () => {
					goto(urls.PROJECT.QUESTIONS(o.id.toString()), { noScroll: true });
				}
			},
			pane: {
				type: 'component'
			}
		});

	o.nbNews > 0 &&
		tabs.push({
			key: 'news',
			title: {
				label: get(t)('operation.tabs.news'),
				nbItems: o.nbNews,
				onClick: () => {
					goto(urls.PROJECT.NEWS(o.id.toString()), { noScroll: true });
				}
			},
			pane: {
				type: 'component'
			}
		});

	o.nbTestimonials > 0 &&
		tabs.push({
			key: 'temoignages',
			title: {
				label: get(t)('operation.tabs.testimonies'),
				nbItems: o.nbTestimonials,
				onClick: () => {
					goto(urls.PROJECT.TESTIMONIES(o.id.toString()), { noScroll: true });
				}
			},
			pane: {
				type: 'component'
			}
		});

	o.nbLives > 0 &&
		tabs.push({
			key: 'lives',
			title: {
				label: get(t)('operation.tabs.lives'),
				nbItems: o.nbLives,
				onClick: () => {
					goto(urls.PROJECT.LIVES(o.id.toString()), { noScroll: true });
				}
			},
			pane: {
				type: 'component'
			}
		});

	return tabs;
};

export const getCardComponent = async (operation: OperationSummaryModel) => {
	const Components: ComponentsObj = {
		FUNDING: async () => await import('@app/components/cards/FundingCard.svelte'),
		VOTE: async () => await import('@app/components/cards/VoteCard.svelte'),
		FUNDED: async () => await import('@app/components/cards/FundedCard.svelte'),
		REFUNDED: async () => await import('@app/components/cards/RefundedCard.svelte')
	};

	return await (
		await Components[operation.status]()
	).default;
};

export const getOperationCollectStatus = (
	params: OperationCollectDataParams
): OperationCollectDataResult => {
	const { walletOk, operation, live } = params;

	const isWalletOk = walletOk;
	let startDate: string | undefined;
	let endDate: string | undefined;
	let operationId: string | undefined;
	let estimatedEndDate: string | undefined;
	let estimatedEndDateCollect: Date | null = null;
	let founding = false;
	let liveId: number | undefined;

	if (operation) {
		startDate = operation.collectStartDate;
		endDate = operation.collectEndDate;
		founding = !!(
			operation.status?.includes(OperationStatusEnum.FUNDING) && !operation.clotureEnCours
		);
		operationId = operation.id.toString();
		estimatedEndDate = operation.estimatedCollectEndDate;
		estimatedEndDateCollect = estimatedEndDate ? new Date(estimatedEndDate) : null;
	}

	if (live) {
		liveId = live.id;
		startDate = live.startDate;
		operationId = live?.operationId?.toString();
	}

	const dateStartCollect = startDate ? parseApiDate(startDate) : undefined;
	const dateEndCollect = endDate ? parseApiDate(endDate) : undefined;

	const collectStarted = dateStartCollect !== undefined && isDateBefore(dateStartCollect);
	const collectEnded = dateEndCollect !== undefined && !isDateAfter(dateEndCollect);

	const printCountdownBlock = !!(
		(founding &&
			((dateEndCollect !== null && !collectEnded) || estimatedEndDateCollect !== null)) ||
		((founding || liveId) && dateStartCollect !== null && !collectStarted)
	);

	return {
		isWalletOk,
		operationId,
		liveId,
		startDate,
		endDate,
		dateStartCollect,
		dateEndCollect,
		collectStarted,
		collectEnded,
		printCountdownBlock
	};
};
export const isPrivateAccessUrl = (url: URL): boolean => {
	return (
		url.searchParams.get('reglementations') === OperationRegulationsEnum.PSI &&
		url.searchParams.get('instruments') ===
			`${OperationFinancialInstrumentEnumFR.ACTIONS},${OperationFinancialInstrumentEnumFR.TITRES_PARTICIPATIFS},${OperationFinancialInstrumentEnumFR.OBLIGATIONS}`
	);
};
export const parseSubscriptionInformationsValue = (value: any): number => {
	return typeof value === 'string' && !isNaN(Number(value)) ? parseInt(value) : 0;
};
export const isOperationSummaryModel = (props: OperationSummaryModel | OperationModel) =>
	'projectName' in props &&
	'projectShortDescriptionFr' in props &&
	'fundRaisingStartDate' in props &&
	'isOngoingClosing' in props;

export const adaptFundingOperationData = (
	props: OperationSummaryModel | OperationModel
): AdaptedOperationModel => {
	const isSummaryModel = isOperationSummaryModel(props);

	const adaptedProps: AdaptedOperationModel = {
		id: props.id.toString(),
		startDate: isSummaryModel ? props.fundRaisingStartDate : props.collectStartDate,
		projectName: isSummaryModel ? props.projectName : props.name,
		projectTypeTarget: isSummaryModel ? props.projectTypeTarget : undefined,
		projectShortDescriptionFr: isSummaryModel ? props.projectShortDescriptionFr : undefined,
		isPremiumOnly: isSummaryModel ? props.isPremiumOnly : undefined,
		projectShortImageCardURL: isSummaryModel
			? (props.projectShortImageCardURL ?? props.projectShortCoverImageURL)
			: props.cover?.url,
		amountNeeded: isSummaryModel
			? getAmountNeededSummary(props)
			: getAmountNeededOperationModel(props),
		amountNeededWiseeders: !isSummaryModel ? props.amountNeededWiseeders : undefined,
		amountNeededPremium: !isSummaryModel ? props.amountNeededPremium : undefined,
		amountReserveInstitutional: !isSummaryModel ? props.amountReserveInstitutional : undefined,
		percentageYieldBond: isSummaryModel ? props.percentageYieldBond : props.percentBondYield,
		follows: isSummaryModel ? props.follows : undefined,
		isOngoingClosing: isSummaryModel ? props.isOngoingClosing : props.clotureEnCours,
		collectEndDate: isSummaryModel ? props.fundRaisingEndDate : props.collectEndDate,
		isCollectComing: isCollectComing(props),
		isCollectEnded: isCollectEnded(props),
		isCollectAlmostEnd: isCollectAlmostEnd(props),
		isCollectRunning: isCollectRunning(props),
		amountSubscribed: props.amountSubscribed,
		outingsHorizon: props.outingsHorizon,
		category: props.category
	};

	return adaptedProps;
};
export const operationCanBeVoted = (operation: OperationModel): boolean => {
	return (
		!operation?.userVote &&
		operation.status?.includes(OperationStatusEnum.VOTE) &&
		!operation.isVoteOngoingAudit &&
		isDateBefore(new Date(), operation.voteEndDate)
	);
};
export const displayPaymentErrorMessage = (status: string) => {
	const $t = get(t);
	const signatureError = urls.SIGNATURE_REDIRECT_URL_ERROR('').split('/').pop();
	const creditCardError = urls.SUBSCRIBE.CREDIT_CARD_PAYMENT_REDIRECT_URL_ERROR('')
		.split('/')
		.pop();
	const sddPaymentError = urls.SUBSCRIBE.SIMPLIFIED_SDD_PAYMENT_REDIRECT_URL_ERROR('')
		.split('/')
		.pop();

	if (status === signatureError) {
		snackbar.open({ message: $t('subscription.signatureErrorMessage') });
	} else if (status === creditCardError || status === sddPaymentError) {
		snackbar.open({ message: $t('subscription.paymentErrorMessage') });
	}
};
